import { useState, useEffect } from 'react'
import { gql, useQuery, useMutation } from '@apollo/client'
import { useParams } from 'react-router'
import { createSearchParams, useNavigate } from 'react-router-dom'
import MuiCarousel from 'react-material-ui-carousel'
import LinearProgress from '@mui/material/LinearProgress'
import Box from '@mui/material/Box'
import { Circle } from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'
import { Dialog, DialogContent, TextField } from '@mui/material'

import {
  BigImageWithText,
  Button,
  CardBox,
  CharitiesSearchInput,
  Container,
  Grid,
  Icons,
  RecentAndFavouriteCharities,
  Stack,
  Text,
} from '../components'
import { toAmountString } from '../utils'
import { useUserIsOnMobileApp } from '../hooks'
import { useBrandingContext } from '../context/brandingProvider'
import { queries, mutations } from '../graphql'
import { DashboardRenderer } from '../components/dashboard'
import palette from '../palette.json'

export function MyGivingFund() {
  return <DashboardRenderer dashboardKey="donor" />
}

function Carousel() {
  const branding = useBrandingContext()
  const instanceBrandingId = branding?.id ? parseInt(branding.id, 10) : null

  const { data: { carousel = [] } = {}, loading } = useQuery(
    gql`
      query Carousel($instanceBrandingId: Int) {
        carousel(instanceBrandingId: $instanceBrandingId) {
          title
          body
          footer
          link
          imageUrl
        }
      }
    `,
    { variables: { instanceBrandingId } }
  )
  if (loading) return null
  if (!carousel.length) return <PlaceholderCarouselItem />

  return (
    <CardBox
      sx={{
        p: 0,
        width: '90%',
        margin: '0 auto',
        aspectRatio: '1208 / 636',
      }}
    >
      <MuiCarousel
        navButtonsAlwaysInvisible
        sx={{
          width: '100%',
          height: '100%',
          'button:hover svg': { fill: 'white' },
        }}
        interval={12000}
        duration={200}
        indicatorContainerProps={{
          style: {
            display: carousel.length < 2 ? 'none' : undefined,
            position: 'absolute',
            width: 'auto',
            bottom: '6px',
            right: '6px',
            zIndex: '1',
          },
        }}
        activeIndicatorIconButtonProps={{
          sx: {
            svg: { fill: '#fffa' },
          },
        }}
        indicatorIconButtonProps={{
          sx: {
            svg: { fill: '#3333', stroke: '#fff9' },
          },
        }}
        IndicatorIcon={<Circle sx={{ fontSize: '100%' }} />}
      >
        {carousel
          /* eslint-disable react/no-array-index-key */
          .map(({ title, body, footer, link, imageUrl }, index) => (
            <BigImageWithText key={index} title={title} body={body} footer={footer} link={link} imageUrl={imageUrl} />
          ))}
      </MuiCarousel>
    </CardBox>
  )
}

function PlaceholderCarouselItem() {
  return (
    <BigImageWithText title="2023, What a year of giving!" footer="Read our Newsletter" link="https://givewise.ca" />
  )
}

function BigShareFundsLink() {
  const { fundId } = useParams()
  const theme = useTheme()
  return (
    <CardBox
      sx={{
        backgroundImage: `radial-gradient(circle at top left, ${theme.palette.secondary.light}, ${theme.palette.secondary.main})`,
        color: 'primary.dark',
        height: '100%',
      }}
    >
      <Stack alignItems="center" height="100%">
        <Icons.Gift style={{ fontSize: '48px' }} />
        <Text.Bold textAlign="center">
          Give the gift of giving and <span style={{ whiteSpace: 'nowrap' }}>Share Funds</span>
        </Text.Bold>
        <Text.Body textAlign="center" flexGrow="1">
          It is the perfect gift for any occasion, like a charitable gift card. You choose the amount and send via
          email. Once your recipient receives it, they choose a charity to gift the money to.
        </Text.Body>
        <Button to={`/funds/${fundId}/share-funds-with-others`}>Share Funds</Button>
      </Stack>
    </CardBox>
  )
}

function GivingGoalsComponent() {
  const { fundId } = useParams()
  const theme = useTheme()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [goalAmount, setGoalAmount] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const {
    data: { me: { fund = {} } = {} } = {},
    loading,
    refetch,
  } = useQuery(queries.funds.myFund, {
    variables: { fundId: parseInt(fundId, 10) },
  })

  const [updateGivingGoal] = useMutation(mutations.funds.updateGivingGoal, {
    onCompleted: () => {
      refetch()
      setDialogOpen(false)
      setIsLoading(false)
    },
    onError: (error) => {
      console.error('Error updating giving goal:', error)
      setIsLoading(false)
    },
  })

  const { givingGoals, netTotalContributionsGivingYTD = 0 } = fund

  const currentGoalAmount = givingGoals?.amount || 0
  const currentGiving = netTotalContributionsGivingYTD
  const progressPercentage = currentGoalAmount > 0 ? Math.min((currentGiving / currentGoalAmount) * 100, 100) : 0

  useEffect(() => {
    if (dialogOpen && currentGoalAmount > 0) {
      setGoalAmount(currentGoalAmount.toString())
    }
  }, [dialogOpen, currentGoalAmount])

  const handleOpenDialog = () => {
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setGoalAmount('')
  }

  const handleSaveGoal = async () => {
    if (!goalAmount || parseFloat(goalAmount) <= 0) {
      return
    }

    setIsLoading(true)

    try {
      await updateGivingGoal({
        variables: {
          data: {
            fundId: parseInt(fundId, 10),
            amount: parseFloat(goalAmount),
          },
        },
      })
    } catch (error) {
      console.error('Error saving goal:', error)
      setIsLoading(false)
    }
  }

  if (loading) {
    return (
      <CardBox sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Text.Body>Loading...</Text.Body>
      </CardBox>
    )
  }

  const hasGoal = currentGoalAmount > 0

  return (
    <>
      <CardBox
        sx={{
          backgroundImage: `radial-gradient(circle at top left, ${theme.palette.primary.light}, ${theme.palette.common.white})`,
          color: 'primary.dark',
          height: '100%',
        }}
      >
        <Stack alignItems="center" height="100%">
          <Icons.Gift style={{ fontSize: '48px', color: '#1a2e52' }} />

          {hasGoal ? (
            <>
              <Text.H2 sx={{ fontWeight: 'bold', color: '#1a2e52' }} textAlign="center">
                Progress Towards Your Giving Goal
              </Text.H2>
              <GivingGoalsProgressBar value={progressPercentage} />
              <Text.H6 textAlign="center" flexGrow="1" sx={{ color: palette.text }}>
                ${Math.round(currentGiving).toLocaleString('en-US')} of your $
                {Math.round(currentGoalAmount).toLocaleString('en-US')} goal
              </Text.H6>
              <Button onClick={handleOpenDialog}>
                <Text.AltBody sx={{ fontWeight: 'bold' }}>Update Your Giving Goal</Text.AltBody>
              </Button>
            </>
          ) : (
            <>
              <Text.H2 sx={{ fontWeight: 'bold', color: '#1a2e52' }} textAlign="center">
                Set Your Giving Goal
              </Text.H2>
              <GivingGoalsProgressBar value={progressPercentage} />
              <Text.Body textAlign="center" flexGrow="1" sx={{ mt: 2, color: palette.text }}>
                Setting a giving goal helps you stay motivated and track your charitable impact throughout the year.
              </Text.Body>
              <Button onClick={handleOpenDialog}>
                <Text.AltBody sx={{ fontWeight: 'bold' }}>Set Your Giving Goal</Text.AltBody>
              </Button>
            </>
          )}
        </Stack>
      </CardBox>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
            minHeight: '400px',
          },
        }}
      >
        <DialogContent sx={{ p: 0, height: '400px' }}>
          <Grid container spacing={0} sx={{ height: '100%' }}>
            {/* Left Column */}
            <Grid
              sx={{
                backgroundColor: 'primary.dark',
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
              item
              xs={6}
            >
              <Stack spacing={2} alignItems="center">
                <Text.Bold variant="h4" textAlign="center" sx={{ color: 'white' }}>
                  Let&apos;s put your generosity in motion.
                </Text.Bold>

                <img
                  src="/givewise-logo-wordmark.png"
                  alt="GWIcon"
                  style={{
                    width: '128px',
                    height: '128px',
                    objectFit: 'cover',
                    objectPosition: 'left center',
                    filter: 'brightness(0) invert(1)',
                  }}
                />

                <Text.Body textAlign="center" sx={{ color: 'common.white' }}>
                  GiveWise donors who set a goal at the start of the year tend to give more—and they feel more connected
                  to the causes they care about.
                </Text.Body>
              </Stack>
            </Grid>

            {/* Right Column */}
            <Grid
              sx={{
                p: 5,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
              item
              xs={6}
            >
              <Stack spacing={3}>
                <div>
                  <Text.H5 sx={{ color: 'primary.light', mb: 4 }}>What&apos;s your generosity goal this year?</Text.H5>
                  <Text.Bold variant="h6">Set it—or adjust it—anytime!</Text.Bold>
                </div>

                <TextField
                  fullWidth
                  placeholder="Enter Giving Goal"
                  value={goalAmount}
                  onChange={(e) => setGoalAmount(e.target.value)}
                  type="number"
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: <span>$&nbsp;</span>,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />

                <Stack spacing={2}>
                  <Button
                    variant="contained"
                    onClick={handleSaveGoal}
                    size="large"
                    disabled={isLoading || !goalAmount || parseFloat(goalAmount) <= 0}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '16px',
                      width: '100%',
                      backgroundColor: 'secondary.main',
                      '&:hover': { backgroundColor: 'secondary.light' },
                    }}
                  >
                    <Text.H6 sx={{ color: 'secondary.contrastText' }}>{isLoading ? 'Saving...' : 'Set Goal'}</Text.H6>
                  </Button>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  )
}

function GivingGoalsProgressBar({ value }) {
  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <LinearProgress
        variant="determinate"
        value={value}
        sx={{
          height: 50,
          borderRadius: 10,
          backgroundColor: 'primary.main',
          '& .MuiLinearProgress-bar': {
            backgroundColor: 'secondary.main',
            borderRadius: 10,
          },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        <Text.H4
          sx={{
            color: 'secondary.contrastText',
            fontWeight: 'bold',
            textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
          }}
        >{`${Math.round(value)}%`}</Text.H4>
      </Box>
    </Box>
  )
}

function FundStatistics() {
  const { fundId } = useParams()
  const { data: { me: { fund = {} } = {} } = {} } = useQuery(queries.funds.myFund, {
    variables: { fundId: parseInt(fundId, 10) },
  })
  const {
    netTotalGifts,
    netTotalGiftsYTD,
    netTotalGiftsPreviousYear,
    netTotalContributionsGiving,
    netTotalContributionsInvestment,
    netTotalContributionsGivingYTD,
    netTotalContributionsGivingPreviousYear,
    netTotalContributionsInvestmentYTD,
    uniqueCharitiesCount,
  } = fund
  if (!fund) return null
  return (
    <Stack>
      <Text.H2>History</Text.H2>
      <CardBox>
        {/* Total Contributions */}
        <Container
          sx={{
            border: '2px solid',
            borderColor: 'primary.light',
            borderRadius: 2,
            padding: 2,
            bgcolor: 'grey.100',
          }}
        >
          <Stack spacing={1}>
            <Text.Bold>Total Fund Contributions</Text.Bold>
            <Text.AltBody>
              Year to Date (YTD):{' '}
              <b>{toAmountString(netTotalContributionsGivingYTD + netTotalContributionsInvestmentYTD)}</b>
            </Text.AltBody>
            <Text.AltBody>
              Previous Year:{' '}
              <b>{toAmountString(netTotalContributionsGivingPreviousYear + netTotalContributionsInvestmentYTD)}</b>
            </Text.AltBody>
            <Text.AltBody>
              Lifetime: <b>{toAmountString(netTotalContributionsGiving + netTotalContributionsInvestment)}</b>
            </Text.AltBody>
            <Button size="small" to="contribute">
              Make a Contribution
            </Button>
          </Stack>
        </Container>

        {/* Total Gifts */}
        <Container
          sx={{
            border: '2px solid',
            borderColor: 'primary.light',
            borderRadius: 2,
            padding: 2,
            bgcolor: 'grey.100',
          }}
        >
          <Stack spacing={1}>
            <Text.Bold>Total Gifts</Text.Bold>
            <Text.AltBody>
              Year to Date (YTD): <b>{toAmountString(netTotalGiftsYTD)}</b>
            </Text.AltBody>
            <Text.AltBody>
              Previous Year: <b>{toAmountString(netTotalGiftsPreviousYear)}</b>
            </Text.AltBody>
            <Text.AltBody>
              Lifetime: <b>{toAmountString(netTotalGifts)}</b>
            </Text.AltBody>
            <Text.AltBody>
              Number of Charities Gifted To: <b>{uniqueCharitiesCount}</b>
            </Text.AltBody>
            <Button size="small" to="grant">
              Give to Charity
            </Button>
          </Stack>
        </Container>
      </CardBox>
    </Stack>
  )
}

function GiveToCharity() {
  const navigate = useNavigate()
  const { fundId } = useParams()
  const [searchText, setSearchText] = useState('')

  const onSubmit = (event) => {
    event.preventDefault()
    const search = createSearchParams({ s: searchText }).toString()
    navigate({ pathname: `/funds/${fundId}/grant/search-by-text`, search })
  }

  return (
    <Stack alignItems="stretch">
      <Text.H2>Give To Charity</Text.H2>

      <form onSubmit={onSubmit}>
        <CharitiesSearchInput onChange={(e) => setSearchText(e.target.value)} />
      </form>

      <RecentAndFavouriteCharities />
    </Stack>
  )
}
